/*!

=========================================================
* Material Dashboard PRO React - v1.7.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import _ from 'lodash';
// react plugin for creating charts
import Chartist from "chartist";
import ChartistGraph from "react-chartist";

import { makeStyles } from '@material-ui/styles';

import { useSelector, useDispatch } from 'react-redux';
import { useFirestoreConnect } from "react-redux-firebase";

import Icon from "@material-ui/core/Icon";

// core components
import {
  Container,
  Row,
  Col
} from 'reactstrap';

import Table from "components/Table/Table.jsx";
import Button from "components/CustomButtons/Button.jsx";
import Danger from "components/Typography/Danger.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import CardText from "components/Card/CardText";

import { ROLES_STRING } from 'constants/user';

import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from 'actions';


import {
  dailySalesChart,
  emailsSubscriptionChart,
  completedTasksChart
} from "variables/charts";

import dashboardStyle from "assets/jss/material-dashboard-pro-react/views/dashboardStyle";

import enbrainImage from "assets/img/enbrain.png";
import hanologo from "assets/img/hanohyuplogo.png";

import { ACTIVITY_LEVEL_LABELS } from "constants/form";

import {
  dangerColor,
  infoColor,
  grayColor,
} from "assets/jss/material-dashboard-pro-react.jsx";
import { getOrgsAction } from "actions";
import ctTargetLine from "./utils/ChartistUtils";
import NotEnoughData from "./NotEnoughData";

const useStyles = makeStyles({
  ...dashboardStyle
})

function DashboardForOrg({ org_id: _org_id, location, history }) {
  console.log('location(DashboardForOrg) = ', location);
  const org_id = _org_id || location?.state?.org_id;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getOrgsAction());
    return () => {
      // cleanup
    }
  }, []);

  const classes = useStyles();

  useFirestoreConnect([
    `statistics/${org_id}`
  ]);

  const statisticsData = useSelector(({ firestore: { data } }) => data.statistics && data.statistics[org_id]);
  const currentRole = useSelector(state => state.auth.currentRole);
  const currentOrg = useSelector(state => state.org.currentOrg);

  console.log('통계데이터 = ', statisticsData);

  const demographicPivotInfo = () => {
    const { demographicPivot = [] } = statisticsData;
    const grouped_demographicPivot = _.groupBy(demographicPivot, "age_group");

    const sumCount1 = Object.keys(grouped_demographicPivot).map(key =>
      _(grouped_demographicPivot[key])
        .filter(obj => obj.gender === 1)
        .sumBy('count'));

    const sumCount0 = Object.keys(grouped_demographicPivot).map(key =>
      _(grouped_demographicPivot[key])
        .filter(obj => obj.gender === 0)
        .sumBy('count'));

    return {
      data: {
        labels: Object.keys(grouped_demographicPivot),
        series: [
          { "name": "남", "data": sumCount1 },
          { "name": "여", "data": sumCount0 }
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        }
      }
    }
  }

  const programGenderPivotInfo = () => {
    const { userCount = {} } = statisticsData;
    const {
      gamesTestUserCountByOrgGender = [],
      platformUserCountByOrgGender = [],
      counselorsCountByOrgGender = {}
    } = userCount;


    return {
      data: {
        labels: ['상담사', '엔브레인 플랫폼', '엔브레인 게임즈', '치매', '우울증', '스트레스'],
        series: [
          {
            "name": "남", "data": [
              counselorsCountByOrgGender['1'],
              _.find(platformUserCountByOrgGender, { gender: '1' })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: 'E_', gender: 1 })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: '치매', gender: 1 })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: '우울', gender: 1 })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: '스트', gender: 1 })?.user_count || 0,
            ]
          },
          {
            "name": "여", "data": [
              counselorsCountByOrgGender['0'],
              _.find(platformUserCountByOrgGender, { gender: '0' })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: 'E_', gender: 0 })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: '치매', gender: 0 })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: '우울', gender: 0 })?.user_count || 0,
              _.find(gamesTestUserCountByOrgGender, { game_id: '스트', gender: 0 })?.user_count || 0,
            ]
          }
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
        ]
      }
    }
  }

  const getDominantActivityLevel = () => {
    const { activityLevelPivot = [] } = statisticsData;
    const each = _.groupBy(activityLevelPivot, 'activity_level')
    const counts = Object.values(each).map((item, index) => ({
      title: ACTIVITY_LEVEL_LABELS[index],
      count: _.sumBy(item, 'count')
    }));
    console.log('each = ', each);
    const dominantActivity = _.maxBy(counts, 'count');
    return dominantActivity?.title;
  }

  const activityLevelPivotInfo = () => {
    const { activityLevelPivot = [] } = statisticsData;
    const grouped_activityLevelPivot = _.groupBy(activityLevelPivot, "activity_level");
    console.log("grouped_activityLevelPivot = ", grouped_activityLevelPivot);

    const sumCount1 = Object.keys(grouped_activityLevelPivot).map(key =>
      _(grouped_activityLevelPivot[key])
        .filter(obj => obj.gender === 1)
        .sumBy('count'));

    const sumCount0 = Object.keys(grouped_activityLevelPivot).map(key =>
      _(grouped_activityLevelPivot[key])
        .filter(obj => obj.gender === 0)
        .sumBy('count'));


    return {
      data: {
        labels: ACTIVITY_LEVEL_LABELS,
        series: [
          sumCount1,
          sumCount0
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
          // legendPlugin({
          //   legendNames: ['남', '여']
          // })
        ]
      }
    }
  }

  // 2-3 교육수준별, 간잉정신상태 검사
  const mmse_dsPivotInfo = () => {
    const { mmse_dsPivot = [] } = statisticsData;
    const grouped_mmse_dsPivot = _.groupBy(mmse_dsPivot, "educated_years");
    console.log("grouped_mmse_dsPivot = ", grouped_mmse_dsPivot);

    const sumCount1 = Object.keys(grouped_mmse_dsPivot).map(key =>
      _(grouped_mmse_dsPivot[key])
        .filter(obj => obj.gender === 1)
        .meanBy('avg_score'));

    const sumCount0 = Object.keys(grouped_mmse_dsPivot).map(key =>
      _(grouped_mmse_dsPivot[key])
        .filter(obj => obj.gender === 0)
        .meanBy('avg_score'));


    return {
      data: {
        labels: ["0~3년", "4~6년(초졸)", "7~12년(중고졸)", "13년이상(대학이상)"],
        series: [
          sumCount1,
          sumCount0
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
          ctTargetLine()
        ],
        targetLine: {
          value: avg_mmse_ds(),
          class: 'ct-target-line-warning'
        },
      }
    }
  }

  // 2-3 연령대별 간이정신상태검사
  const mmse_dsByAgeGroupPivotInfo = () => {
    const { mmse_dsPivotByAgeGroup = [] } = statisticsData;
    const grouped_stat = _.groupBy(mmse_dsPivotByAgeGroup, "age_group");

    const sumCount1 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 1)
        .meanBy('avg_score'));

    const sumCount0 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 0)
        .meanBy('avg_score'));

    return {
      data: {
        labels: Object.keys(grouped_stat),
        series: [
          sumCount1,
          sumCount0
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
          ctTargetLine()
        ],
        targetLine: {
          value: avg_mmse_ds(),
          class: 'ct-target-line-warning'
        },
      }
    }
  }

  const s_iadlPivotInfo = () => {
    const { s_iadlPivotByAgeGroup = [] } = statisticsData;
    const grouped_stat = _.groupBy(s_iadlPivotByAgeGroup, "age_group");

    const sumCount1 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 1)
        .meanBy('avg_score'));

    const sumCount0 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 0)
        .meanBy('avg_score'));

    return {
      data: {
        labels: Object.keys(grouped_stat),
        series: [
          sumCount1,
          sumCount0
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
          ctTargetLine()
        ],
        targetLine: {
          value: avg_siadl(),
          class: 'ct-target-line-warning'
        },
      }
    }
  }

  const s_adlPivotInfo = () => {
    const { s_adlPivotByAgeGroup = [] } = statisticsData;
    const grouped_stat = _.groupBy(s_adlPivotByAgeGroup, "age_group");

    const sumCount1 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 1)
        .meanBy('avg_score'));

    const sumCount0 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 0)
        .meanBy('avg_score'));

    return {
      data: {
        labels: Object.keys(grouped_stat),
        series: [
          sumCount1,
          sumCount0
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
          ctTargetLine()
        ],
        targetLine: {
          value: avg_sadl(),
          class: 'ct-target-line-warning'
        },
      }
    }
  }

  const gds_shortPivotInfo = () => {
    const { gds_shortPivotByAgeGroup = [] } = statisticsData;
    const grouped_stat = _.groupBy(gds_shortPivotByAgeGroup, "age_group");

    const sumCount1 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 1)
        .meanBy('avg_score'));

    const sumCount0 = Object.keys(grouped_stat).map(key =>
      _(grouped_stat[key])
        .filter(obj => obj.gender === 0)
        .meanBy('avg_score'));

    return {
      data: {
        labels: Object.keys(grouped_stat),
        series: [
          sumCount1,
          sumCount0
        ]
      },
      options: {
        axisX: {
          showGrid: true
        },
        chartPadding: {
          top: 0,
          right: 5,
          bottom: 0,
          left: 0
        },
        plugins: [
          ctTargetLine()
        ],
        targetLine: {
          value: avgGDS_Short(),
          class: 'ct-target-line-warning'
        },
      }
    }
  }

  // const genderPieChartInfo = () => {

  //   const { genderRatio = [] } = statisticsData;
  //   return {
  //     data: {
  //       labels: genderRatio.map(obj => obj.gender === 1 ? "남" : "여"),
  //       series: genderRatio.map(obj => ({ value: obj.rate }))
  //     },
  //     options: {
  //       // height: "100px",
  //       // donut: true
  //       chartPadding: 30,
  //       labelOffset: 40,
  //       labelDirection: 'explode'
  //     }
  //   }
  // }

  // const agesPieChartInfo = () => {
  //   const { ageRangeRatio = [] } = statisticsData;

  //   return {
  //     data: {
  //       labels: ageRangeRatio.map(obj => obj.age_group),
  //       series: ageRangeRatio.map(obj => obj.rate),
  //     },
  //     options: {
  //       // height: "100px",
  //       // donut: true
  //       chartPadding: 30,
  //       labelOffset: 40,
  //       labelDirection: 'explode'
  //     }
  //   }
  // }

  // const activityLevelChartInfo = () => {
  //   const { activityLevelRatio = [] } = statisticsData;
  //   return {
  //     data: {
  //       labels: activityLevelRatio.map(obj => ACTIVITY_LEVEL_LABELS[obj.activity_level]),
  //       series: activityLevelRatio.map(obj => obj.rate),
  //     },
  //     options: {
  //       // height: "100px",
  //       // donut: true
  //       chartPadding: 30,
  //       labelOffset: 40,
  //       labelDirection: 'explode'
  //     }
  //   }
  // }

  const avg_siadl = () => {
    const { avgS_IADL = [] } = statisticsData;
    if (avgS_IADL.length > 0) {
      return Math.round(avgS_IADL[0].avg_score);
    } else {
      return "loading...";
    }
  }
  const avg_sadl = () => {
    const { avgS_ADL = [] } = statisticsData;
    if (avgS_ADL.length > 0) {
      return Math.round(avgS_ADL[0].avg_score);
    } else {
      return "loading...";
    }
  }
  const avg_mmse_ds = () => {
    const { avgMMSE_DS = [] } = statisticsData;
    if (avgMMSE_DS.length > 0) {
      return Math.round(avgMMSE_DS[0].avg_score) || "N/A";
    } else {
      return "loading...";
    }
  }

  const avgGDS_Short = () => {
    const { avgGDS_Short = [] } = statisticsData;
    if (avgGDS_Short.length > 0) {
      return Math.round(avgGDS_Short[0].avg_score) || "N/A";
    } else {
      return "loading...";
    }
  }

  return (org_id && statisticsData)
    ? (
      <Container>
        <Row>
          <Col>
            <Row>
              <Col className='d-inline-flex'>
                <div style={{
                  backgroundColor: dangerColor[0],
                  borderRadius: 10,
                  width: 20,
                  height: 20,
                  marginRight: "8px"
                }}></div><span>{currentOrg?.get('기관명')}</span>
              </Col>
            </Row>
          </Col>
          <Col xs={12} className={classes.grid}>
            <Card>
              <CardHeader color="danger" stats text className={classes.cardHeader}>
                <CardText color="danger">
                  <div className={classes.titleLogoContainer}>
                    <img src={hanologo} className={classes.titleLogo} />
                    <h3 className={classes.cardTitle}>
                      엔브레인플랫폼 가입회원
                    </h3>
                  </div>
                </CardText>
              </CardHeader>
              <CardBody>
                <Row align="center">
                  <Col xs={12}>
                    <table style={{ flex: 1, width: '100%' }}>
                      <thead>
                        <tr>
                          <th></th>
                          <th>총인원수(명)</th>
                          <th>엔브레인플랫폼</th>
                          <th>두뇌활력프로그램<br />엔브레인</th>
                          <th>정신건강테스트<br />치매</th>
                          <th>정신건강테스트<br />우울증</th>
                          <th>정신건강테스트<br />스트레스</th>
                        </tr>
                      </thead>
                      <tbody className='text-center'>
                        <tr>
                          <td className='text-left'>기관상담사</td>
                          <td>{statisticsData.userCount?.counselorsCount}</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                        </tr>
                        <tr>
                          <td className='text-left'>기관회원누적수</td>
                          <td>{statisticsData.userCount?.totalUserCount}</td>
                          <td>{statisticsData.userCount?.platformUserCountByOrg}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountByOrg[0]?.user_count}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountByOrg[1]?.user_count}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountByOrg[3]?.user_count}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountByOrg[2]?.user_count}</td>
                        </tr>
                        <tr>
                          <td className='text-left'>기관신규회원누적수(월)</td>
                          <td>{statisticsData.userCount?.totalUserCountOfThisMonth}</td>
                          <td>{statisticsData.userCount?.platformUserCountOfThisMonthByOrg}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountOfThisMonthByOrg[0]?.user_count}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountOfThisMonthByOrg[1]?.user_count}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountOfThisMonthByOrg[3]?.user_count}</td>
                          <td>{statisticsData.userCount?.gamesTestsUserCountOfThisMonthByOrg[2]?.user_count}</td>
                        </tr>
                        <tr>
                          <td className='text-left'>기관탈퇴회원 누적수</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                        </tr>
                      </tbody>
                    </table>

                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="danger" stats text className={classes.cardHeader}>
                <CardText color="danger">
                  <div className={classes.titleLogoContainer}>
                    <img src={hanologo} className={classes.titleLogo} />
                    <h3 className={classes.cardTitle}>
                      기관상담사
                    </h3>
                  </div>
                </CardText>
              </CardHeader>
              <CardBody>
                <Row align="center">
                  <Col xs={12}>
                    <div style={{
                      color: dangerColor[0],
                      fontSize: '2.5em',
                      fontWeight: '400',
                    }}>{statisticsData.userCount?.counselorsCount}</div>
                    <div style={{
                      color: grayColor[0],
                      marginTop: 10,
                      fontSize: '0.9em',
                    }}>{currentOrg?.get('기관명')} 사회복지사(명)</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="danger" stats text className={classes.cardHeader}>
                <CardText color="danger">
                  <div className={classes.titleLogoContainer}>
                    <img src={hanologo} className={classes.titleLogo} />
                    <h3 className={classes.cardTitle}>
                      엔브레인플랫폼 회원수
                    </h3>
                  </div>
                </CardText>
              </CardHeader>
              <CardBody>
                <Row align="center">
                  <Col xs={12}>
                    <div style={{
                      color: dangerColor[0],
                      fontSize: '2.5em',
                      fontWeight: '400',
                    }}>{statisticsData.userCount?.totalUserCount}</div>
                    <div style={{
                      color: grayColor[0],
                      marginTop: 10,
                      fontSize: '0.9em',
                    }}>{currentOrg?.get('기관명')} 엔브레인플랫폼 회원 누적수(명)</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="danger" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="danger">
                    <div className={classes.titleLogoContainer}>
                      <img src={hanologo} className={classes.titleLogo} />
                      <h3 className={classes.cardTitle}>
                        프로그램별 사용현황
                    </h3>
                    </div>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                </div>
                </div>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={programGenderPivotInfo().data}
                  type="Bar"
                  options={programGenderPivotInfo().options}
                />
              </CardBody>
              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 엔브레인플랫폼 사용자 등록 현황 <br />
                  <Icon>info</Icon> 구성에 따른 성별대비
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="danger" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="danger">
                    <div className={classes.titleLogoContainer}>
                      <img src={hanologo} className={classes.titleLogo} />
                      <h3 className={classes.cardTitle}>
                        프로그램별 연령대 현황
                    </h3>
                    </div>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                </div>
                </div>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={demographicPivotInfo().data}
                  type="Bar"
                  options={demographicPivotInfo().options}
                />
                {
                  _.sumBy(demographicPivotInfo().data.series, 'data') === 0
                    ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                    : null
                }

              </CardBody>
              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 엔브레인플랫폼 사용자 등록 현황 <br />
                  <Icon>info</Icon> 구성에 따른 연령대별 현황
                </div>
              </CardFooter>
            </Card>
          </Col>

          <Col xs={12}>
            <Row>
              <Col className='d-inline-flex'>
                <div style={{
                  backgroundColor: infoColor[0],
                  borderRadius: 10,
                  width: 20,
                  height: 20,
                  marginRight: "8px"
                }}></div><span>노인맞춤돌봄서비스 사례관리</span>
              </Col>
            </Row>
          </Col>

          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리1]초기상담기록
                     </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                  </div>
                </div>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>기관 등록 회원: {statisticsData.userCount?.totalUserCount}</div>
                </div>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={demographicPivotInfo().data}
                  type="Bar"
                  options={demographicPivotInfo().options}
                />
                {
                  statisticsData.userCount?.totalUserCount === 0
                    ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                    : null
                }
              </CardBody>
              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 초기상담기록지 <br />
                  <Icon>info</Icon> 연령대별 남녀성비 통계
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리2-2]활동수준점검
                    </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendWarningBox} />비율<div className={classes.legendInfoBox} />남(인원) <div className={classes.legendDangerBox} />여(인원)
                  </div>
                </div>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>{getDominantActivityLevel()}</div>
                </div>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={activityLevelPivotInfo().data}
                  type="Bar"
                  options={activityLevelPivotInfo().options}
                />
              </CardBody>
              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 활동수준점검 <br />
                  <Icon>info</Icon> 활동수준별 남녀비율 통계
                </div>
              </CardFooter>
              {
                !getDominantActivityLevel()
                  ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                  : null
              }
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리2-3]간이정신상태 검사
                    </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendWarningBox} />MMSE-DS<div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                  </div>
                </div>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>평균점수: {avg_mmse_ds()}점</div>
                </div>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={mmse_dsPivotInfo().data}
                  type="Bar"
                  options={mmse_dsPivotInfo().options}
                />
              </CardBody>
              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 간이정신상태 검사 교육년수 별 성별 인원 <br />
                  <Icon>info</Icon> 교육년수 별 성별 인원 및 MMSE-DS 평균 점수 통계
                </div>
              </CardFooter>
              {
                avg_mmse_ds() === 'N/A'
                  ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                  : null
              }
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>

                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리2-3]간이정신상태 검사
                    </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendWarningBox} />MMSE-DS<div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                  </div>
                </div>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>평균점수: {avg_mmse_ds()}점</div>
                </div>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={mmse_dsByAgeGroupPivotInfo().data}
                  type="Bar"
                  options={mmse_dsByAgeGroupPivotInfo().options}
                />
              </CardBody>
              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 간이정신상태 검사 연령 별 성별 인원 <br />
                  <Icon>info</Icon> 연령별 성별 인원 및 MMSE-DS 평균 점수 통계
                </div>
              </CardFooter>
              {
                avg_mmse_ds() === 'N/A'
                  ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                  : null
              }
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리2-6]수단적 일상생활활동
                    </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendWarningBox} />전체평균<div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>전체 평균 점수: {avg_siadl()}</div>
                </div>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={s_iadlPivotInfo().data}
                  type="Bar"
                  options={s_iadlPivotInfo().options}
                />
              </CardBody>

              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 수단적일상생활활동 <br />
                  <Icon>info</Icon> 수단적 일상생활활동 전체 평균 점수 대비 기관별 평균 점수 통계
                </div>
              </CardFooter>
              {
                avg_siadl() === 0
                  ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                  : null
              }
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리2-7]기본적 일상생활활동
                    </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendWarningBox} />전체평균<div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>전체 평균 점수: {avg_sadl()}</div>
                </div>

                <ChartistGraph
                  className={'ct-double-octave'}
                  data={s_adlPivotInfo().data}
                  type="Bar"
                  options={s_adlPivotInfo().options}
                />
              </CardBody>

              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 기본적 일상생활활동 <br />
                  <Icon>info</Icon> 기본적 일상생활활동 전체 평균 점수 대비 기관별 평균 점수 통계
                </div>
              </CardFooter>
              {
                avg_sadl() === 0
                  ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                  : null
              }
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} className={classes.grid}>
            <Card>
              <CardHeader color="info" stats text className={classes.cardHeader}>
                <div className={classes.rowDirection}>
                  <CardText color="info">
                    <h3 className={classes.cardTitle}>
                      [사례관리2-10]정서
                    </h3>
                  </CardText>
                  <div className={classes.legendBox}>
                    <div className={classes.legendWarningBox} />전체평균<div className={classes.legendInfoBox} />남 <div className={classes.legendDangerBox} />여
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                <div className={classes.averageArea}>
                  <div className={classes.averageBox}>전체 평균 점수: {avgGDS_Short()}</div>
                </div>
                <ChartistGraph
                  className={'ct-double-octave'}
                  data={gds_shortPivotInfo().data}
                  type="Bar"
                  options={gds_shortPivotInfo().options}
                />
              </CardBody>

              <CardFooter stats className={classes.cardFooter}>
                <div className={classes.bottomDescription}>
                  <Icon>info</Icon> 정서 <br />
                  <Icon>info</Icon> 정서 전체 평균 점수 대비 기관별 평균 점수 통계
                </div>
              </CardFooter>
              {
                avgGDS_Short() === 'N/A'
                  ? <NotEnoughData style={{ marginLeft: '10%', marginTop: '15%' }} />
                  : null
              }
            </Card>
          </Col>
        </Row>
      </Container>
    )
    : <Container>
      <Row>
        <Col className='text-center text-warning'>
          <h4>통계자료가 생성되지 않아서 보여줄 수 없습니다.</h4>
        </Col>
      </Row>
    </Container>
}

DashboardForOrg.propTypes = {
  org_id: PropTypes.string
};

export default DashboardForOrg;